import React, {useEffect, useState, createContext, useContext, useCallback, useMemo} from 'react';
import {AppstoreOutlined} from "@ant-design/icons";
import {NavBar} from "antd-mobile";
import {MessageOutline} from "antd-mobile-icons";
import {ScanOutlined} from "@ant-design/icons";
import {Toast} from "antd-mobile";
import {PackageIcon, TaskIcon, MapModeIcon} from "assets/icons";
import {Item, PathNavigationTabBar} from "../components/PathNavigationTabBar";
import {Outlet, useNavigate, useLocation} from "react-router-dom";
import UserMenuDrawer from '../components/UserMenuDrawer';
import MenuIconWithBadge from "../components/MenuIconWithBadge";
import DeliveryDataService from "../../../services/DeliveryDataService";
import {DeliveringPath, DspPath, TaskPath, DeliveryDetailPath} from "../../../enums/DeliveryNavigatePathEnum";
import {isDspManager, getUserInfo} from "../../../utils/auth";
import {t} from "../../../i18n";
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {
    isAppVersionGreaterThanOrEqual,
    kDoNotPassZipCodeListVersion,
    kIOSNativeDeliveryVersion,
    kNativeMapModeVersion
} from 'utils/versionUtils';
import { useDeliveryTaskOrderList, useRouteColorMap } from '../store/deliveryStore';
import { openNativeMapMode, sendZipCodeList, reloadNativeWebLocal} from 'utils/appRuntime';
import { nativeCallbacks, NativeCallbackManager, DeliverySuccessParams } from 'utils/NativeCallbackManager';
import {useQuery} from "@apollo/client";
import { GET_DELIVERING_ZIPCODES_BY_DRIVER_ACCOUNT_ID, GetDeliveringZipcodesByDriverAccountIdQuery } from 'graphql/queries/deliveryQueries';
import PODAbnormalAlert from '../../../components/PODAbnormalAlert';
import {isSmsTemplateGrayEnabled} from "../../../utils/gray/SmsTemplateModelGray";
import { client } from "graphql/client";
import { GET_DRIVER_IS_NOT_READ_COUNT } from '../delivering/chat/graphql/queries/getDriverIsNotReadCount';
import {WEB_TEMPORARY_GRAY_ENABLED, checkTemporaryGrayDriver} from "../../../utils/grayscaleDriverUtils";

interface DeliveryLayoutContextType {
    refreshFailedCount: () => Promise<void>;
    failedCount: number;
    podAlertHeight: number;
}

export const DeliveryLayoutContext = createContext<DeliveryLayoutContextType>({
    refreshFailedCount: async () => {},
    failedCount: 0,
    podAlertHeight: 0
});

// 自定义Hook，方便子组件使用
export const useDeliveryLayout = () => useContext(DeliveryLayoutContext);

type DeliveryLayoutProps = {
    children?: React.ReactNode;
    title: string;
}

// localStorage key

/**
 * 检查是否启用Web临时页面灰度
 * @returns boolean - true表示在灰度内，false表示不在灰度内
 */
const isWebTemporaryGrayEnabled = (): boolean => {
    const grayEnabled = localStorage.getItem(WEB_TEMPORARY_GRAY_ENABLED);
    return grayEnabled === 'true';
};

const DeliveryLayout: React.FC<DeliveryLayoutProps> = ({children, title}) => {
    const [menuVisible, setMenuVisible] = useState(false);
    const visitorFlag = isDspManager();
    const [failedCount, setFailedCount] = useState<number>(0);
    const [podAlertHeight, setPodAlertHeight] = useState<number>(0);
    const navigate = useNavigate();
    const location = useLocation();

    // 灰度控制：检查是否启用Web临时页面灰度
    const isGrayEnabled = isWebTemporaryGrayEnabled();
    // 版本号检查：检查版本号是否 >= kNativeMapModeVersion
    const [isVersionSatisfied, setIsVersionSatisfied] = useState<boolean>(false);
    
    useEffect(() => {
        isAppVersionGreaterThanOrEqual(kNativeMapModeVersion)
            .then((result: boolean) => {
                setIsVersionSatisfied(result);
            })
            .catch((error: any) => {
                console.error('Failed to check app version:', error);
                setIsVersionSatisfied(false);
            });
    }, []);
    
    // 打印 isGrayEnabled 值
    console.log('isGrayEnabled:', isGrayEnabled);
    console.log('isVersionSatisfied:', isVersionSatisfied); // 打印版本是否满足
    // 组合条件：版本号满足 && 灰度为 true → 走新路由，否则走旧路由
    const shouldUseNewRoute = isGrayEnabled && isVersionSatisfied;
    //const shouldUseNewRoute = true
    console.log('shouldUseNewRoute1:', shouldUseNewRoute);
    // 根据灰度控制逻辑动态生成 defaultItems
    const defaultItems: Item[] = useMemo(() => [
        {
            path: shouldUseNewRoute ? "/delivery/delivering/temporary" : DeliveringPath.DELIVERY_DELIVERING,
            icon: <PackageIcon/>,
            label: `${t('pages.itemDelivery')}`
        },
        {
            path: TaskPath.DELIVERY_TASK,
            icon: <TaskIcon/>,
            label: `${t('pages.itemTask')}`
        }
    ], [shouldUseNewRoute]);
    const {deliveryTaskOrderList} = useDeliveryTaskOrderList();
    const {getOrAssignRouteColor} = useRouteColorMap();
    // 获取当前司机的zipcode列表
    const {data: zipCodeList} = useQuery<GetDeliveringZipcodesByDriverAccountIdQuery>(GET_DELIVERING_ZIPCODES_BY_DRIVER_ACCOUNT_ID);
    const userInfo = getUserInfo();
    const driverId = useMemo(() => Number(userInfo?.account?.id ?? 0), [userInfo?.account?.id]);
    // 在组件内部
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    // 添加未读消息计数状态
    const [unreadCount, setUnreadCount] = useState(0);
    const dspId = deliveryTaskOrderList.length > 0 ? deliveryTaskOrderList[0].dspId : 1;
    // 获取未读消息计数
    useEffect(() => {
        // 只有在灰度开关开启时才获取未读消息计数
        if (!isEnabled || !driverId) return;
        
        const fetchUnreadCount = async () => {
            try {
                const result = await client.query({
                    query: GET_DRIVER_IS_NOT_READ_COUNT,
                    variables: { driverId },
                    fetchPolicy: 'no-cache'
                });
                
                setUnreadCount(result.data?.getDriverIsNotReadCount || 0);
            } catch (error) {
                console.error('Failed to fetch unread count:', error);
            }
        };

        // 初始获取
        fetchUnreadCount();
        

    }, [driverId,isEnabled]);

    useEffect(() => {
        async function checkGrayStatus() {
            try {
                const result = await isSmsTemplateGrayEnabled(dspId, driverId);
                setIsEnabled(result);
            } finally {
                setLoading(false);
            }
        }

        checkGrayStatus();
    }, [dspId, driverId]);


    useEffect(()=>{
         handleFailedCount();
    },[title])

    // 监听iOS主动触发的登出事件
    useEffect(() => {
        const handleLogoutEvent = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { redirectTo } = customEvent.detail || {};
            console.log('[DeliveryLayout] 收到iOS登出事件，执行客户端路由跳转:', redirectTo);
            navigate(redirectTo || '/login', { replace: true });
        };

        window.addEventListener('app:logout', handleLogoutEvent);

        return () => {
            window.removeEventListener('app:logout', handleLogoutEvent);
        };
    }, [navigate])

    const handleFailedCount = async () => {
        try {
            // 使用await正确处理异步调用
            const count = await DeliveryDataService.getDeliveryOrderCount();
            setFailedCount(count);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        }
    };

    const refreshFailedCount = useCallback(async () => {
        await handleFailedCount();
    }, [visitorFlag]);

    const handleOpenMenu = () => {
        setMenuVisible(true);
    };
    
    const handleCloseMenu = () => {
        setMenuVisible(false);
    };

    // 地图模式注册原生回调
    useEffect(() => {
        const handleDeliverySuccess = (params?: DeliverySuccessParams) => {
            const currentTrackingNumber = params?.trackingNumber || "";
            const deliveryTaskOrderId = params?.deliveryTaskOrderId || 0;
            navigate(DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber), {
                state: {
                    deliveryTaskOrderId: deliveryTaskOrderId,
                    trackingNumber: currentTrackingNumber,
                    detailType: 'delivered',
                    fromNativeMapMode: true
                }
            });
        };

        const handleDeliveryFailed = (params?: any) => {
            const currentTrackingNumber = params?.trackingNumber || "";
            const deliveryTaskOrderId = params?.deliveryTaskOrderId || 0;
            const exceptionReason = params?.exceptionReason;
            navigate(DeliveringPath.DELIVERY_MARK_EXCEPTION, {
                state: {
                    deliveryTaskOrderId: deliveryTaskOrderId,
                    trackingNumber: currentTrackingNumber,
                    exceptionReason: exceptionReason,
                    fromNativeMapMode: true
                }
            });
        };

        // 注册回调
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccess);
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailed);

        return () => {
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccess);
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailed);
        };
    }, [navigate]);

    // 传递zipCodeList到原生端
    useEffect(() => {
        const sendZipCodesToNative = async () => {
            const shouldNotPassZipCodeList = await isAppVersionGreaterThanOrEqual(kDoNotPassZipCodeListVersion);
            if (shouldNotPassZipCodeList) {
                return;
            }

            const zipcodes = zipCodeList?.getDeliveringZipcodesByDriverAccountId || [];
            if (zipcodes.length > 0) {
                const supportsNativeMap = await isAppVersionGreaterThanOrEqual(kIOSNativeDeliveryVersion);
                if (supportsNativeMap) {
                    sendZipCodeList(zipcodes);
                }
            }
        };

        sendZipCodesToNative();
    }, [zipCodeList]);

    // 刷新web本地存储
    useEffect(() => {
        reloadNativeWebLocal()
    }, []);

    // 检查当前是否在delivery页面（需要显示地图图标）
    const isDeliveryPage = location.pathname.startsWith(DeliveringPath.DELIVERY_DELIVERING);

    const handleMapModeClick = async () => {
        sessionStorage.setItem('mapModeEntrySource', 'delivering-list');
        navigate(shouldUseNewRoute ? "/delivery/temporary/map-mode" : DeliveringPath.DELIVERY_MAP_MODE);
    };

    const handleChatListClick = () => {
        // 跳转到聊天列表页面
        navigate(DeliveringPath.DELIVERY_CHAT_LIST);
    };

    const contextValue = {
        refreshFailedCount,
        failedCount,
        podAlertHeight
    };
    

    return (
        <DeliveryLayoutContext.Provider value={contextValue}>
            <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
                {/* 用户菜单抽屉 */}
                <UserMenuDrawer
                    visible={menuVisible}
                    onClose={handleCloseMenu}
                    failedCount = {failedCount}
                />
                
                {/* 顶部导航栏 - 固定 */}
                <div {...getNavBarContainerStyle('flex-none sticky top-0 z-10 bg-white shadow-sm')}>
                    <NavBar
                       backIcon={<MenuIconWithBadge count={failedCount} />}
                       onBack={handleOpenMenu}
                       right={isDeliveryPage ? (
                           <div className="flex items-center justify-end w-full gap-3">
                               { !loading && isEnabled && (
                                   <div onClick={handleChatListClick} className="cursor-pointer relative">
                                       <MessageOutline fontSize={24} />
                                       {unreadCount > 0 && (
                                           <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
                                               <span className="text-xs text-white font-medium">
                                                   {unreadCount > 99 ? '99+' : unreadCount}
                                               </span>
                                           </div>
                                       )}
                                   </div>
                               )}
                               <div onClick={handleMapModeClick} className="cursor-pointer">
                                   <MapModeIcon width={24} height={24} />
                               </div>
                           </div>
                       ) : null}>
                        {title}
                    </NavBar>
                </div>

                {/* 中间内容区域 - 弹性滚动 */}
                <div className="flex-1 overflow-hidden">
                    {/* 875: POD异常提示暂时隐藏 */}
                    {/* {!visitorFlag && (
                        <PODAbnormalAlert
                            navigateState={{ from: location.pathname }}
                            onHeightChange={setPodAlertHeight}
                        />
                    )} */}
                    {children || <Outlet />}
                </div>

                {/* 底部导航 - 固定 */}
                <div className="flex-none sticky bottom-0 z-10 bg-white border-t border-gray-200">
                    <PathNavigationTabBar items={defaultItems}/>
                </div>
            </div>
        </DeliveryLayoutContext.Provider>
    );
};

export default DeliveryLayout;